import { useEffect, useMemo, useState } from 'react'
import { Smartphone, ShieldCheck, Check } from 'lucide-react'
import { useMe } from './shell/AppShell.js'
import { adminMutate } from './admin/primitives.js'

/**
 * HANDOFF D'AUTHENTIFICATION MOBILE (LOT 9.1).
 *
 * /mobile/auth/start — ouverte par l'app dans le NAVIGATEUR SYSTÈME.
 * L'utilisateur s'authentifie avec le parcours Web EXISTANT (mot de passe,
 * TOTP, passkey, codes de secours — rien de dupliqué), puis AUTORISE
 * explicitement la connexion de l'appareil. Un code à usage unique (2 min,
 * hashé, lié au state anti-CSRF et au challenge PKCE) part vers
 * /mobile/auth/callback — JAMAIS un token dans une URL.
 *
 * Les paramètres sont persistés en sessionStorage AVANT la redirection de
 * login (return_to ne conserve pas la query).
 */

const KEY = 'gestion.mobile-auth'

function readParams(): { state: string; challenge: string } | null {
  const q = new URLSearchParams(window.location.search)
  const state = q.get('state')
  const challenge = q.get('challenge')
  if (state && challenge) {
    sessionStorage.setItem(KEY, JSON.stringify({ state, challenge }))
    return { state, challenge }
  }
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as { state: string; challenge: string } | null
    return saved
  } catch { return null }
}

export function MobileAuthStartPage() {
  const me = useMe()
  const params = useMemo(readParams, [])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!params) {
    return (
      <main className="lc-page lc-stagger" style={{ maxWidth: 560 }}>
        <h1 className="lc-title">Connexion mobile</h1>
        <p className="lc-subtitle">
          Cette page s’ouvre depuis l’application mobile Gestion. Lancez la
          connexion depuis l’application.
        </p>
      </main>
    )
  }

  const authorize = async () => {
    setBusy(true)
    const r = await adminMutate<{ code?: string; error?: string; message?: string }>(
      'POST', '/api/v1/mobile-auth/authorize',
      { state: params.state, code_challenge: params.challenge },
    )
    if (r.status < 300 && r.data.code) {
      sessionStorage.removeItem(KEY)
      window.location.assign(`/mobile/auth/callback?code=${encodeURIComponent(r.data.code)}&state=${encodeURIComponent(params.state)}`)
      return
    }
    setBusy(false)
    setError(r.data.message ?? 'L’autorisation a échoué — réessayez depuis l’application.')
  }

  return (
    <main className="lc-page lc-stagger" style={{ maxWidth: 560 }}>
      <h1 className="lc-title">Connexion mobile</h1>
      <p className="lc-subtitle">Autoriser l’application Gestion sur votre téléphone ?</p>

      <section className="lc-panel gd-panel" style={{ marginTop: 12 }}>
        <div className="lc-panel__body" style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Smartphone size={22} style={{ color: 'var(--violet)' }} aria-hidden="true" />
            <div className="lc-stack" style={{ gap: 2 }}>
              <strong>{me ? me.display_name : 'Votre compte Gestion'}</strong>
              <span className="lc-hint">{me?.email ?? ''}</span>
            </div>
          </div>
          <p className="lc-hint" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <ShieldCheck size={15} style={{ flex: 'none', marginTop: 2 }} aria-hidden="true" />
            L’application recevra une session à votre nom, avec vos permissions.
            Vous pourrez la révoquer à tout moment depuis Profil → Appareils.
          </p>
          {error ? <p className="lc-helper lc-helper--error" role="alert">{error}</p> : null}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="lc-btn lc-btn--primary" disabled={busy || !me} onClick={authorize}>
              <Check size={15} aria-hidden="true" /> Autoriser la connexion
            </button>
            <button className="lc-btn" onClick={() => window.close()}>Annuler</button>
          </div>
          {!me ? <p className="lc-hint">Connexion à Gestion en cours…</p> : null}
        </div>
      </section>
    </main>
  )
}

export function MobileAuthCallbackPage() {
  const q = new URLSearchParams(window.location.search)
  const code = q.get('code') ?? ''
  const state = q.get('state') ?? ''
  const appUrl = `gestion://auth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`

  useEffect(() => {
    // L'app intercepte ce lien (Universal Link) — repli : schéma custom
    // pour les builds de développement non encore associées au domaine.
    if (code && state) {
      const t = setTimeout(() => { window.location.replace(appUrl) }, 400)
      return () => clearTimeout(t)
    }
    return undefined
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="lc-page lc-stagger" style={{ maxWidth: 560, textAlign: 'center' }}>
      <h1 className="lc-title">Retour vers l’application…</h1>
      <p className="lc-subtitle">
        {code ? 'La connexion est autorisée. Si rien ne se passe :' : 'Lien incomplet — relancez la connexion depuis l’application.'}
      </p>
      {code ? (
        <p style={{ marginTop: 12 }}>
          <a className="lc-btn lc-btn--primary" href={appUrl}>Ouvrir l’application Gestion</a>
        </p>
      ) : null}
    </main>
  )
}
